import { SignInPrompt } from '@/features/account/SignInPrompt';
import { EmailVerificationNotice } from '@/features/account/EmailVerificationNotice';
import { VenueDealCard } from '@/features/venues/deals/VenueDealCard';
import { EventPoster } from '@/features/events/shared/EventPoster';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { ErrorState, LoadingState } from '@/components/ui/async-state';
import { Button } from '@/components/ui/button';
import { Icon } from '@/components/ui/icon';
import { UsernameBadge } from '@/components/ui/UsernameBadge';
import { AppScreen, Card, IconAction } from '@/components/ui/scene';
import { Text } from '@/components/ui/text';
import { useAuth } from '@/lib/auth/auth';
import { useAccountContext } from '@/features/workspaces/useAccountContext';
import type { DiscoveryEvent as RadiousEvent, VenuePublicDeal } from '@/lib/api/apiTypes';
import { sortDealsByTiming } from '@/features/venues/deals/deals';
import { trackImageLoadFailed } from '@/lib/analytics/posthog';
import { shareLink } from '@/lib/platform/share';
import { DESIGN_TOKENS } from '@/lib/design/theme';
import { useDashboard } from '@/features/profile/useDashboard';
import { getBusinessShellHref } from '@/features/workspaces/workspace';
import { cn } from '@/lib/utils';
import { formatEventDayLabel, formatEventTime, getEventLiveCutoffMs } from '@/features/events/eventFormatting';
import { getProfileInitials } from '@/features/profile/profileFormatting';
import { buildPublicIdentityLink, getEventHref, getVenueHref } from '@/lib/navigation/routes';
import { useIsDesktop } from '@/hooks/useBreakpoint';
import { Link, router } from 'expo-router';
import {
  BookmarkIcon,
  CalendarCheckIcon,
  CalendarIcon,
  CalendarPlusIcon,
  LockIcon,
  Settings2Icon,
  ShareIcon,
} from 'lucide-react-native';
import * as React from 'react';
import { FlatList, Pressable, ScrollView, View, type ListRenderItem } from 'react-native';

const PROFILE_PLACEHOLDER_BIO =
  'Shape your profile with the rooms you host, the plans you save, and the people who show up for your signal.';
const PROFILE_EVENT_DATE_FORMATTER = new Intl.DateTimeFormat('en-US', {
  month: 'short',
  day: 'numeric',
  weekday: 'short',
});

type SectionKey = 'going' | 'saved' | 'hosted';

function isCurrentProfileEvent(event: Pick<RadiousEvent, 'starts_at' | 'ends_at'>) {
  return getEventLiveCutoffMs(event.starts_at, event.ends_at) > Date.now();
}

export default function ProfileScreen() {
  const session = useAuth((s) => s.session);
  const { currentContext, currentBusinessMembership } = useAccountContext();
  const isDesktop = useIsDesktop();
  const dashboardQuery = useDashboard();
  const scrollViewRef = React.useRef<ScrollView | null>(null);
  const sectionWasChosenRef = React.useRef(false);
  const error: string | null = null;
  const [sectionOffsets, setSectionOffsets] = React.useState<Record<SectionKey, number>>({
    going: 0,
    saved: 0,
    hosted: 0,
  });
  const [activeSection, setActiveSection] = React.useState<SectionKey>('going');
  const dashboard = dashboardQuery.data ?? null;
  const isLoading = Boolean(session) && dashboardQuery.isPending;
  const isStaff = dashboard?.profile.is_staff ?? session?.me.profile.is_staff ?? false;
  const hostedEvents = React.useMemo(
    () => (dashboard?.hosted_events ?? []).filter(isCurrentProfileEvent),
    [dashboard?.hosted_events]
  );
  const goingEvents = React.useMemo(
    () => (dashboard?.going_events ?? []).filter(isCurrentProfileEvent),
    [dashboard?.going_events]
  );
  const savedEvents = React.useMemo(
    () => (dashboard?.saved_events ?? []).filter(isCurrentProfileEvent),
    [dashboard?.saved_events]
  );
  const savedDeals = React.useMemo(
    () => sortDealsByTiming(dashboard?.saved_deals ?? []),
    [dashboard?.saved_deals]
  );
  const bio = dashboard?.profile.bio?.trim() ?? '';
  const hasBio = bio.length > 0 && bio !== PROFILE_PLACEHOLDER_BIO;
  const showBusinessAdminLinks =
    currentContext?.kind === 'business' &&
    Boolean(currentBusinessMembership) &&
    (currentBusinessMembership?.role === 'owner' || currentBusinessMembership?.role === 'manager');
  const activeBusinessVenueName =
    currentContext?.kind === 'business'
      ? currentBusinessMembership?.venues.find(
          (venue) => venue.venue_id === currentContext.activeVenueId
        )?.venue_name
      : null;
  const nextPlan = goingEvents[0] ?? null;
  const visibleInterestTags = (dashboard?.profile.interest_tags ?? []).slice(0, 4);

  React.useEffect(() => {
    if (sectionWasChosenRef.current) {
      return;
    }

    if (activeSection === 'going' && goingEvents.length > 0) {
      return;
    }

    if (goingEvents.length > 0) {
      setActiveSection('going');
      return;
    }

    if (savedEvents.length > 0 || savedDeals.length > 0) {
      setActiveSection('saved');
      return;
    }

    if (hostedEvents.length > 0) {
      setActiveSection('hosted');
    }
  }, [activeSection, goingEvents.length, hostedEvents.length, savedDeals.length, savedEvents.length]);

  function scrollToSection(section: SectionKey) {
    scrollViewRef.current?.scrollTo({
      y: Math.max(0, sectionOffsets[section] - 24),
      animated: true,
    });
  }

  function chooseSection(section: SectionKey) {
    sectionWasChosenRef.current = true;
    setActiveSection(section);
  }

  return (
    <AppScreen>
      <ScrollView
        ref={scrollViewRef}
        contentContainerStyle={{ paddingBottom: isDesktop ? 40 : 132 }}
        scrollEventThrottle={16}
        showsVerticalScrollIndicator={false}>
        <View
          style={
            isDesktop
              ? { maxWidth: 900, width: '100%', alignSelf: 'center', paddingHorizontal: 32 }
              : undefined
          }>
          <View className={isDesktop ? 'pt-4' : 'px-4 pt-4'}>
            <View className="flex-row items-center justify-between">
              <Text className="text-3xl font-extrabold tracking-tight">Profile</Text>
              {session && dashboard ? (
                <View className="flex-row items-center gap-3">
                  <IconAction
                    icon={ShareIcon}
                    onPress={() => {
                      const publicLink = buildPublicIdentityLink(dashboard.profile);

                      shareLink({
                        message: publicLink,
                        url: publicLink,
                      }).catch(() => undefined);
                    }}
                  />
                  <IconAction icon={Settings2Icon} onPress={() => router.push('/settings')} />
                </View>
              ) : null}
            </View>

            <EmailVerificationNotice className="mt-4" />
          </View>

          {!session ? (
            <View className={isDesktop ? 'pt-8' : 'px-4 pt-8'}>
              <SignInPrompt
                title="Build your scene identity."
                body="Sign in to manage your profile."
              />
            </View>
          ) : null}

          {isLoading && !dashboard ? <LoadingState label="Loading profile..." /> : null}

          {error ? (
            <ErrorState
              title="Couldn't load profile."
              body={error}
              actionLabel="Retry"
              onAction={() => dashboardQuery.refetch().catch(() => undefined)}
            />
          ) : null}

          {dashboard ? (
            <>
              {isDesktop ? (
                /* ── Desktop: two-column identity card ── */
                <View
                  style={{
                    marginTop: 24,
                    borderRadius: 20,
                    borderWidth: 1,
                    borderColor: DESIGN_TOKENS.colors.borderStrong,
                    backgroundColor: DESIGN_TOKENS.colors.surface,
                    padding: 32,
                    flexDirection: 'row',
                    gap: 32,
                    alignItems: 'flex-start',
                  }}>
                  {/* Left: avatar + name + bio */}
                  <View style={{ flex: 1 }}>
                    <View style={{ flexDirection: 'row', alignItems: 'center', gap: 20 }}>
                      <ProfileAvatar
                        name={dashboard.profile.display_name}
                        avatarURL={dashboard.profile.avatar_url}
                        size={88}
                      />
                      <View>
                        <Text
                          className="text-[28px] font-extrabold tracking-tight"
                          style={{ fontFamily: DESIGN_TOKENS.typography.fonts.bodyBold }}>
                          {dashboard.profile.display_name}
                        </Text>
                        <UsernameBadge
                          username={dashboard.profile.username}
                          isStaff={isStaff}
                          className="mt-1"
                          textClassName="text-muted-foreground"
                        />
                      </View>
                    </View>
                    {hasBio ? (
                      <Text variant="muted" className="mt-5 max-w-[440px] leading-6">
                        {bio}
                      </Text>
                    ) : null}
                  </View>
                  {/* Right: stats column */}
                  <View
                    style={{
                      borderLeftWidth: 1,
                      borderLeftColor: DESIGN_TOKENS.colors.borderStrong,
                      paddingLeft: 32,
                      gap: 24,
                      minWidth: 160,
                    }}>
                    <Pressable
                      onPress={() => {
                        chooseSection('hosted');
                        scrollToSection('hosted');
                      }}>
                      <ProfileStat
                        label="Hosted"
                        value={dashboard.profile.stats.hosted_count}
                        interactive
                      />
                    </Pressable>
                    <Pressable
                      onPress={() => {
                        chooseSection('going');
                        scrollToSection('going');
                      }}>
                      <ProfileStat
                        label="Going"
                        value={dashboard.profile.stats.going_count}
                        interactive
                      />
                    </Pressable>
                    <Pressable
                      onPress={() => {
                        chooseSection('saved');
                        scrollToSection('saved');
                      }}>
                      <ProfileStat
                        label="Saved"
                        value={dashboard.profile.stats.saved_count}
                        interactive
                      />
                    </Pressable>
                    <Pressable
                      onPress={() =>
                        router.push({
                          pathname: '/followers',
                          params: {
                            username: dashboard.profile.username,
                            count: String(dashboard.profile.stats.follower_count),
                          },
                        })
                      }>
                      <ProfileStat
                        label="Followers"
                        value={dashboard.profile.stats.follower_count}
                        interactive
                      />
                    </Pressable>
                  </View>
                </View>
              ) : (
                /* ── Mobile: single-column identity card ── */
                <View className="mt-6 px-4">
                  <Card tone="default" className="p-5">
                    <View className="flex-row items-center gap-4">
                      <ProfileAvatar
                        name={dashboard.profile.display_name}
                        avatarURL={dashboard.profile.avatar_url}
                        size={72}
                      />
                      <View className="flex-1">
                        <Text
                          className="text-2xl font-extrabold tracking-tight"
                          style={{ fontFamily: DESIGN_TOKENS.typography.fonts.bodyBold }}>
                          {dashboard.profile.display_name}
                        </Text>
                        <UsernameBadge
                          username={dashboard.profile.username}
                          isStaff={isStaff}
                          className="mt-1"
                          textClassName="font-semibold text-muted-foreground"
                        />
                      </View>
                    </View>
                    {hasBio ? (
                      <Text variant="muted" className="mt-5 leading-7">
                        {bio}
                      </Text>
                    ) : null}
                    <View
                      className="mt-6 flex-row items-start justify-between rounded-[18px] border px-3 py-4"
                      style={{
                        borderColor: DESIGN_TOKENS.colors.borderStrong,
                        backgroundColor: DESIGN_TOKENS.colors.surfaceAlt,
                      }}>
                      <Pressable
                        onPress={() => {
                          chooseSection('hosted');
                          scrollToSection('hosted');
                        }}>
                        <ProfileStat
                          label="Hosted"
                          value={dashboard.profile.stats.hosted_count}
                          interactive
                        />
                      </Pressable>
                      <Pressable
                        onPress={() => {
                          chooseSection('going');
                          scrollToSection('going');
                        }}>
                        <ProfileStat
                          label="Going"
                          value={dashboard.profile.stats.going_count}
                          interactive
                        />
                      </Pressable>
                      <Pressable
                        onPress={() => {
                          chooseSection('saved');
                          scrollToSection('saved');
                        }}>
                        <ProfileStat
                          label="Saved"
                          value={dashboard.profile.stats.saved_count}
                          interactive
                        />
                      </Pressable>
                      <Pressable
                        onPress={() =>
                          router.push({
                            pathname: '/followers',
                            params: {
                              username: dashboard.profile.username,
                              count: String(dashboard.profile.stats.follower_count),
                            },
                          })
                        }>
                        <ProfileStat
                          label="Followers"
                          value={dashboard.profile.stats.follower_count}
                          interactive
                        />
                      </Pressable>
                    </View>
                  </Card>
                </View>
              )}

              {showBusinessAdminLinks && currentBusinessMembership ? (
                <View className={isDesktop ? 'mt-6' : 'mt-5 px-4'}>
                  <Card tone="default" className="p-5">
                    <Text className="text-base font-semibold text-foreground">Business admin</Text>
                    <Text variant="muted" className="mt-2 leading-6">
                      {activeBusinessVenueName
                        ? `Viewing ${activeBusinessVenueName} under ${currentBusinessMembership.organization_name}.`
                        : `Manage the venue workspace for ${currentBusinessMembership.organization_name}.`}
                    </Text>
                    <View className="mt-4 flex-row flex-wrap gap-3">
                      <Button
                        variant="secondary"
                        className="min-w-[31%] flex-1"
                        onPress={() => router.push(getBusinessShellHref('organization') as never)}>
                        <Text className="font-semibold text-foreground">Venue</Text>
                      </Button>
                    </View>
                  </Card>
                </View>
              ) : null}

              <>
                <ProfileMomentumPanel
                  interestTags={visibleInterestTags}
                  isDesktop={isDesktop}
                  nextPlan={nextPlan}
                />

                <View
                  style={isDesktop ? { marginTop: 32 } : undefined}
                  className={isDesktop ? undefined : 'mt-8 px-4'}
                  onLayout={(event) => {
                    const y = event.nativeEvent?.layout?.y;
                    if (y !== undefined) {
                      setSectionOffsets((current) => ({
                        ...current,
                        going: y,
                        saved: y,
                        hosted: y,
                      }));
                    }
                  }}>
                  <View className="mb-4 mt-2 flex-row items-end justify-between gap-3">
                    <View className="min-w-0 flex-1">
                      <Text variant="large" className="text-xl font-bold">
                        Your Scene
                      </Text>
                      <Text variant="small" className="mt-1 text-muted-foreground">
                        Plans, saved picks, and hosted events.
                      </Text>
                    </View>
                  </View>
                  <ProfileSectionTabs
                    activeSection={activeSection}
                    counts={{
                      going: goingEvents.length,
                      saved: savedEvents.length + savedDeals.length,
                      hosted: hostedEvents.length,
                    }}
                    onChange={(section) => {
                      chooseSection(section);
                    }}
                  />
                  <View className="mt-5">
                    {activeSection === 'going' ? (
                      <EventGrid section="going" events={goingEvents} isDesktop={isDesktop} />
                    ) : null}
                    {activeSection === 'saved' ? (
                      <SavedSection events={savedEvents} deals={savedDeals} isDesktop={isDesktop} />
                    ) : null}
                    {activeSection === 'hosted' ? (
                      <HostedSection events={hostedEvents} isDesktop={isDesktop} />
                    ) : null}
                  </View>
                </View>
              </>
            </>
          ) : null}
        </View>
      </ScrollView>
    </AppScreen>
  );
}

function ProfileAvatar({
  name,
  avatarURL,
  size = 72,
}: {
  name: string;
  avatarURL?: string;
  size?: number;
}) {
  return (
    <Avatar
      alt={name}
      style={{
        width: size,
        height: size,
        borderRadius: size / 2,
        borderWidth: 2,
        borderColor: DESIGN_TOKENS.colors.borderStrong,
        backgroundColor: DESIGN_TOKENS.colors.surfaceAccent,
      }}>
      {avatarURL ? (
        <AvatarImage
          source={{ uri: avatarURL }}
          style={{ width: size, height: size }}
          onError={() => trackImageLoadFailed('profile')}
        />
      ) : (
        <AvatarFallback className="bg-transparent">
          <Text
            className="text-3xl font-extrabold tracking-tight text-foreground"
            style={{ fontFamily: DESIGN_TOKENS.typography.fonts.bodyBold }}>
            {getProfileInitials(name)}
          </Text>
        </AvatarFallback>
      )}
    </Avatar>
  );
}

function ProfileStat({
  label,
  value,
  interactive = false,
}: {
  label: string;
  value: number;
  interactive?: boolean;
}) {
  return (
    <View className="min-w-[68px] items-center">
      <Text
        variant="large"
        className="text-[24px] font-extrabold leading-[24px]"
        style={{ fontFamily: DESIGN_TOKENS.typography.fonts.bodyBold }}>
        {value}
      </Text>
      <Text
        variant="small"
        className="mt-1 text-[11px] uppercase tracking-[0.8px] text-muted-foreground">
        {label}
      </Text>
    </View>
  );
}

function ProfileMomentumPanel({
  interestTags,
  isDesktop,
  nextPlan,
}: {
  interestTags: string[];
  isDesktop: boolean;
  nextPlan: RadiousEvent | null;
}) {
  return (
    <View
      style={isDesktop ? { marginTop: 32 } : undefined}
      className={isDesktop ? undefined : 'mt-8 px-4'}>
      <View className={cn(isDesktop ? 'flex-row gap-4' : 'gap-4')}>
        <Card tone="default" className={cn('p-5', isDesktop && 'flex-[1.4]')}>
          <View className="flex-row items-center gap-2">
            <Icon as={CalendarCheckIcon} size={18} className="text-primary" />
            <Text className="text-base font-semibold text-foreground">Next up</Text>
          </View>
          {nextPlan ? (
            <Link href={getEventHref(nextPlan.id, nextPlan.title)} asChild>
              <Pressable className="mt-4 flex-row gap-4">
                <EventPoster
                  event={nextPlan}
                  className="h-[118px] w-[92px] rounded-[18px]"
                  showMeta={false}
                />
                <View className="min-w-0 flex-1 justify-center">
                  <View className="self-start rounded-full border border-border/70 bg-surface-alt px-3 py-1">
                    <Text variant="small" className="font-semibold uppercase tracking-[1.2px] text-primary">
                      {formatEventDayLabel(nextPlan.starts_at)}
                    </Text>
                  </View>
                  <Text className="mt-3 text-lg font-bold leading-6 text-foreground" numberOfLines={2}>
                    {nextPlan.title}
                  </Text>
                  <Text variant="small" className="mt-1 text-muted-foreground" numberOfLines={1}>
                    {nextPlan.venue_name} · {formatEventTime(nextPlan.starts_at)}
                  </Text>
                </View>
              </Pressable>
            </Link>
          ) : (
            <View className="mt-4 rounded-[18px] border border-border/70 bg-surface-alt/60 p-4">
              <Text className="font-semibold text-foreground">Nothing locked in yet</Text>
              <Text variant="small" className="mt-1 leading-5 text-muted-foreground">
                RSVP to something and it will lead your profile.
              </Text>
              <Button
                variant="secondary"
                className="mt-4 self-start"
                onPress={() => router.push('/(tabs)/events')}>
                <Text className="font-semibold text-foreground">Find a plan</Text>
              </Button>
            </View>
          )}
        </Card>

        <Card tone="default" className={cn('p-5', isDesktop && 'flex-1')}>
          <View className="flex-row items-center gap-2">
            <Icon as={LockIcon} size={17} className="text-muted-foreground" />
            <Text className="text-base font-semibold text-foreground">Personal space</Text>
          </View>
          <Text variant="small" className="mt-3 leading-5 text-muted-foreground">
            Saved picks stay tucked away here. Going and hosted events shape the scene people recognize you for.
          </Text>
          {interestTags.length ? (
            <View className="mt-4 flex-row flex-wrap gap-2">
              {interestTags.map((tag) => (
                <View key={tag} className="rounded-full border border-border/70 bg-surface-alt px-3 py-1.5">
                  <Text variant="small" className="font-semibold text-foreground">
                    {tag}
                  </Text>
                </View>
              ))}
            </View>
          ) : (
            <Button
              variant="secondary"
              className="mt-4 self-start"
              onPress={() => router.push('/settings')}>
              <Text className="font-semibold text-foreground">Add interests</Text>
            </Button>
          )}
        </Card>
      </View>
    </View>
  );
}

function ProfileSectionTabs({
  activeSection,
  counts,
  onChange,
}: {
  activeSection: SectionKey;
  counts: Record<SectionKey, number>;
  onChange: (section: SectionKey) => void;
}) {
  const sections: { key: SectionKey; label: string }[] = [
    { key: 'going', label: 'Going' },
    { key: 'saved', label: 'Saved' },
    { key: 'hosted', label: 'Hosted' },
  ];

  return (
    <View className="flex-row rounded-full border border-border bg-surface p-1">
      {sections.map((section) => {
        const isActive = activeSection === section.key;
        return (
          <Pressable
            key={section.key}
            accessibilityRole="button"
            accessibilityState={isActive ? { selected: true } : undefined}
            className={cn(
              'min-h-11 flex-1 flex-row items-center justify-center gap-2 rounded-full px-3',
              isActive && 'bg-primary'
            )}
            onPress={() => onChange(section.key)}>
            <Text
              variant="small"
              className={cn(
                'font-semibold',
                isActive ? 'text-primary-foreground' : 'text-muted-foreground'
              )}>
              {section.label}
            </Text>
            <Text
              variant="small"
              className={cn(
                'font-semibold',
                isActive ? 'text-primary-foreground/80' : 'text-muted-foreground/70'
              )}>
              {counts[section.key]}
            </Text>
          </Pressable>
        );
      })}
    </View>
  );
}

function HostedSection({ events, isDesktop }: { events: RadiousEvent[]; isDesktop: boolean }) {
  const renderProfileEventCard = React.useCallback<ListRenderItem<RadiousEvent>>(
    ({ item: event }) => (
      <Link href={getEventHref(event.id, event.title)} asChild>
        <Pressable className="w-[160px]">
          <EventPoster
            event={event}
            className="h-[190px] rounded-[20px]"
            showMeta={false}
          />
          <Text
            variant="small"
            className="mt-3 font-semibold text-foreground"
            numberOfLines={2}>
            {event.title}
          </Text>
          <Text variant="small" className="mt-1 text-muted-foreground" numberOfLines={1}>
            {PROFILE_EVENT_DATE_FORMATTER.format(new Date(event.starts_at))}
          </Text>
        </Pressable>
      </Link>
    ),
    []
  );

  return (
    <View>
      {events.length > 0 ? (
        isDesktop ? (
          <View className="flex-row flex-wrap justify-between">
            {events.map((event) => (
              <Link key={event.id} href={getEventHref(event.id, event.title)} asChild>
                <Pressable style={{ width: '31.5%', marginBottom: 20 }}>
                  <EventPoster
                    event={event}
                    className="aspect-[3/4] rounded-[20px]"
                    showMeta={false}
                  />
                  <Text
                    variant="small"
                    className="mt-3 font-semibold text-foreground"
                    numberOfLines={2}>
                    {event.title}
                  </Text>
                  <Text variant="small" className="mt-1 text-muted-foreground" numberOfLines={1}>
                    {PROFILE_EVENT_DATE_FORMATTER.format(new Date(event.starts_at))}
                  </Text>
                </Pressable>
              </Link>
            ))}
          </View>
        ) : (
          <FlatList
            horizontal
            data={events}
            keyExtractor={(e) => e.id}
            showsHorizontalScrollIndicator={false}
            contentContainerStyle={{ gap: 14, paddingRight: 8 }}
            renderItem={renderProfileEventCard}
          />
        )
      ) : (
        <View className="rounded-[22px] border border-border bg-surface p-5">
          <View className="flex-row items-center gap-3">
            <View className="size-10 items-center justify-center rounded-full bg-surface-alt">
              <Icon as={CalendarPlusIcon} size={20} className="text-muted-foreground" />
            </View>
            <View className="min-w-0 flex-1">
              <Text className="font-semibold text-foreground">No events hosted yet</Text>
              <Text variant="small" className="mt-1 text-muted-foreground">
                Hosted events will show here once you publish.
              </Text>
            </View>
          </View>
          <Text variant="small" className="mt-4 leading-5 text-muted-foreground">
            Create your first event and share it with people nearby
          </Text>
          <Button
            className="mt-4 items-center justify-center"
            onPress={() => router.push('/events/create')}>
            <Text>+ Host Event</Text>
          </Button>
        </View>
      )}
    </View>
  );
}

function EventGrid({
  section,
  events,
  isDesktop = false,
}: {
  section: SectionKey;
  events: RadiousEvent[];
  isDesktop?: boolean;
}) {
  if (!events.length) {
    return <ProfileEventEmptyState section={section} />;
  }

  const itemWidth = isDesktop ? '31.5%' : '48.2%';

  return (
    <View className="mt-4 flex-row flex-wrap justify-between">
      {events.map((event) => (
        <Link key={event.id} href={getEventHref(event.id, event.title)} asChild>
          <Pressable style={{ width: itemWidth, marginBottom: 16 }}>
            <EventPoster event={event} className="aspect-square rounded-[18px]" />
            <Text variant="small" className="mt-3 font-semibold text-foreground" numberOfLines={2}>
              {event.title}
            </Text>
            <Text variant="small" className="mt-1 text-muted-foreground" numberOfLines={1}>
              {event.venue_name}
            </Text>
          </Pressable>
        </Link>
      ))}
    </View>
  );
}

function SavedSection({
  deals,
  events,
  isDesktop = false,
}: {
  deals: VenuePublicDeal[];
  events: RadiousEvent[];
  isDesktop?: boolean;
}) {
  if (!events.length && !deals.length) {
    return <ProfileEventEmptyState section="saved" />;
  }

  return (
    <View>
      {events.length ? <EventGrid section="saved" events={events} isDesktop={isDesktop} /> : null}

      {deals.length ? (
        <View className={events.length ? 'mt-5' : 'mt-4'}>
          <Text variant="large" className="mb-4 mt-2 text-xl font-bold">
            Saved Deals
          </Text>
          <View className={isDesktop ? 'flex-row flex-wrap justify-between' : 'gap-3'}>
            {deals.map((deal) => (
              <View key={deal.id} className={isDesktop ? 'mb-4 w-[31.5%]' : undefined}>
                <VenueDealCard
                  deal={deal}
                  onPress={() =>
                    router.push(
                      getVenueHref(
                        deal.venue_id,
                        { deal: deal.id, source: 'profile' },
                        { name: deal.venue_name }
                      )
                    )
                  }
                />
              </View>
            ))}
          </View>
        </View>
      ) : null}
    </View>
  );
}

function ProfileEventEmptyState({ section }: { section: SectionKey }) {
  const isGoing = section === 'going';
  const IconGlyph = isGoing ? CalendarIcon : BookmarkIcon;
  const title = isGoing ? 'Nothing going yet' : 'Nothing saved yet';
  const body = isGoing
    ? 'Find something tonight and RSVP to lock it in'
    : 'Tap the bookmark on any event to save it for later';
  const actionLabel = isGoing ? 'Explore events' : 'Browse the feed';

  return (
    <View className="items-center px-4 py-10">
      <Icon as={IconGlyph} size={32} className="text-muted-foreground" />
      <Text className="mt-4 text-center text-foreground">{title}</Text>
      <Text variant="small" className="mt-2 text-center text-muted-foreground">
        {body}
      </Text>
      <Button
        className="mt-6 items-center justify-center"
        onPress={() => router.push('/(tabs)/events')}>
        <Text>{actionLabel}</Text>
      </Button>
    </View>
  );
}
